<div
  #entryPageContainer
  class="h-[calc(100vh-2rem)] w-full caption flex flex-col scrollbar relative"
  (scroll)="onContainerScroll($event)"
  [ngClass]="
    onboardingStore.inProgress() ? 'pointer-events-none overflow-hidden' : ''
  "
>
  @if (loading.saveEmail) {
    <maximizer-loader type="legacy"></maximizer-loader>
  }

  @if (loading.skeleton) {
    <div class="sticky top-0 pt-3 pb-1 px-5">
      <div class="flex items-center">
        <maximizer-outlook-skeleton
          containerClasses="h-12 w-12 max-w-12 rounded-full bg-neutral-20 mr-4"
          fullWidth="none"
        ></maximizer-outlook-skeleton>
        <maximizer-outlook-skeleton
          containerClasses="h-8 w-full rounded-md bg-neutral-20"
          fullWidth="full"
        ></maximizer-outlook-skeleton>
      </div>
      <maximizer-outlook-skeleton
        class="my-4"
        containerClasses="h-8 w-full rounded-md bg-neutral-20"
        fullWidth="full"
      ></maximizer-outlook-skeleton>
      <maximizer-outlook-skeleton
        class="mb-4"
        containerClasses="h-4 w-full rounded-md bg-neutral-20"
        fullWidth="full"
      ></maximizer-outlook-skeleton>
      <div class="flex mb-4 justify-around">
        @for (item of [0, 1, 2, 3, 4]; track $index) {
          <div class="flex">
            <maximizer-outlook-skeleton
              containerClasses="h-8 w-8 max-w-8 rounded-full bg-neutral-20"
              fullWidth="none"
            ></maximizer-outlook-skeleton>
          </div>
        }
      </div>
    </div>
  } @else {
    <div class="sticky top-0 flex flex-col gap-2 pt-3 pb-1 px-5">
      <!-- Entry row -->
      <div class="flex gap-2 justify-between items-start">
        <div class="flex gap-2">
          <div
            class="avatar avatar-xl text-black"
            [ngClass]="getAvatarClasses()"
          >
            @if (abentry && abentry.type !== EntryType.Company) {
              <span class="large-avatar-text">{{
                outlookEmail | outlookAvatarLetters
              }}</span>
            } @else if (lead && lead.email && isUser(lead.email)) {
              <span class="large-avatar-text">{{
                outlookEmail | outlookAvatarLetters
              }}</span>
            } @else if (lead && (!lead.email || !isUser(lead.email))) {
              <span
                class="fa-icons icon-solid icon-28 icon-user-magnifying-glass cursor-pointer m-auto"
              ></span>
            } @else if (abentry && abentry.type === EntryType.Company) {
              <span
                class="fa-icons icon-regular icon-31 icon-building m-auto cursor-pointer"
              ></span>
            }
          </div>

          <div class="flex flex-col py-1">
            <p class="subtitle-1 break-all">
              {{ abentry?.name ?? lead?.name }}
            </p>
            <p class="subtitle-2 break-all">
              {{ abentry?.position ?? lead?.position }}
            </p>
            @if (abentry && abentry.parentKey) {
              <p class="caption break-all">
                {{ abentry.parentDisplayValue }}
              </p>
            } @else {
              <p class="break-all no-underline">
                {{ abentry?.companyName ?? lead?.companyName }}
              </p>
            }
          </div>
        </div>

        @if (abentry?.type !== EntryType.Company) {
          <div>
            <kendo-dropdownbutton
              id="outlook-entry-ui-entryPage-entryActionsToggle"
              #entryActionsDropdown
              [data]="entryActions"
              themeColor="tertiary"
              iconClass="fa-icons icon-solid icon-14 icon-ellipsis-vertical px-1"
              [popupSettings]="{
                appendTo: viewContainerRef,
              }"
            >
              <ng-template kendoDropDownButtonItemTemplate let-dataItem>
                <div
                  kendoTooltip
                  [tooltipTemplate]="tooltipTemplate"
                  position="top"
                  [callout]="false"
                  [showOn]="dataItem.disabled ? 'hover' : 'none'"
                  [title]="dataItem.title ?? ''"
                  id="outlook-entry-ui-entryPage-action{{ dataItem.tag }}"
                  class="action-dropdown flex items-center gap-2 w-full"
                  [style.opacity]="dataItem.disabled ? '0.5' : '1'"
                  [style.cursor]="dataItem.disabled ? 'default' : 'pointer'"
                >
                  <span [class]="dataItem.icon" class="w-4"></span>
                  <span>{{ dataItem.text }}</span>
                </div>
              </ng-template>
            </kendo-dropdownbutton>
          </div>
        }
      </div>

      <!-- Info row -->
      <div>
        @if (loading.skeleton) {
          <maximizer-loader type="legacy"></maximizer-loader>
        }

        @if (abentry && userRights()?.opportunities?.read) {
          <div class="flex">
            <span class="text-caption text-neutral-90"
              >{{
                'outlook.entryPage.currentOpportunities'
                  | translate
                    : {
                        label: 'outlook.entries.opportunities' | translate,
                      }
              }}:
            </span>
            <span class="text-caption text-neutral-70 ml-1">{{
              opportunities?.current ?? ''
            }}</span>
          </div>
          <div class="flex">
            <p class="text-caption text-neutral-90">
              @if (outlookStore.vertical() === Vertical.WM) {
                {{
                  'outlook.entryPage.completedOpportunities'
                    | translate
                      : {
                          verb: 'outlook.entryPage.managed' | translate,
                          label: 'outlook.entries.opportunities' | translate,
                        }
                }}:
              } @else {
                {{
                  'outlook.entryPage.completedOpportunities'
                    | translate
                      : {
                          verb: 'outlook.entryPage.completed' | translate,
                          label: 'outlook.entries.opportunities' | translate,
                        }
                }}:
              }
            </p>
            <p class="text-caption text-neutral-70 ml-1">
              {{ opportunities?.completed ?? '' }}
            </p>
          </div>
        }

        <div class="flex">
          <span class="text-caption text-neutral-90"
            >{{ 'outlook.entryPage.lastContactedDate' | translate }}:
          </span>
          @if (lastContactDate(); as lastContact) {
            <span class="text-caption text-neutral-70 ml-1">
              {{ lastContact | date: 'MMM dd, yyyy' }}
            </span>
          } @else {
            <span class="text-caption text-neutral-70 ml-1">{{
              'outlook.entryPage.notContacted' | translate
            }}</span>
          }
        </div>

        @if (lead && lead.status === 3) {
          <div class="flex">
            <span class="text-caption text-neutral-90"
              >{{ 'outlook.entryPage.archivedDate' | translate }}:
            </span>
            <span class="text-caption text-neutral-70 ml-2">{{
              lead.archivedDate | date: 'MMM dd, yyyy'
            }}</span>
          </div>
        }
      </div>

      <!-- Auto-Save Email Toggle -->
      @if (outlookStore.versionValidForNewSync() && outlookStore.isCloud()) {
        @if (showAutoSaveEmailToggle && !outlookStore.isSharedMailbox()) {
          <!-- Onboarding anchor | Step 3: Guide 3  -->
          <span
            kendoPopoverContainer
            [popover]="entryActions3"
            #entryActions3Anchor
            #entryActions3Container="kendoPopoverContainer"
            showOn="none"
            class="self-start"
          ></span>
          <div class="flex gap-1.5 items-center">
            <div class="relative inline-flex">
              @if (
                onboardingStore.inProgress() &&
                (onboardingStore.currentGuide() === StepGuideId.EntryActions3 ||
                  onboardingStore.currentGuide() ===
                    StepGuideId.EntryActions3Alt)
              ) {
                <span
                  class="absolute top-[-6px] left-[-7px] flex h-3 w-3 pointer-events-none z-10"
                >
                  <span
                    class="animate-ping absolute inline-flex h-full w-full rounded-full bg-grass-green-120 opacity-75"
                  ></span>
                  <span
                    class="relative inline-flex rounded-full h-3 w-3 bg-grass-green-120"
                  ></span>
                </span>
              }
              <span
                kendoTooltip
                [tooltipTemplate]="installOutlookSyncTooltip"
                [tooltipWidth]="250"
                [callout]="false"
                position="bottom"
                [closable]="true"
                [showOn]="outlookSyncInstalled ? 'none' : 'click'"
              >
                @let autoSaveTooltipModeValue = autoSaveTooltipMode();
                @let autoSaveTooltipTemplate =
                  autoSaveTooltipModeValue === 'syncEverything'
                    ? autoSave
                      ? autoSaveSyncEverythingEnabledTooltip
                      : autoSaveSyncEverythingExcludedTooltip
                    : autoSaveTooltipModeValue === 'disabled'
                      ? autoSaveDisabledTooltip
                      : tooltipTemplate;
                <kendo-switch
                  kendoTooltip
                  id="outlook-entry-ui-entryPage-autoSave"
                  size="small"
                  [(ngModel)]="autoSave"
                  [disabled]="
                    autoSaveDisabled() || loading.sync || !emailSaveUserRights
                  "
                  [ngClass]="
                    onboardingStore.inProgress() &&
                    (onboardingStore.currentGuide() ===
                      StepGuideId.EntryActions3 ||
                      onboardingStore.currentGuide() ===
                        StepGuideId.EntryActions3Alt)
                      ? 'pointer-events-auto'
                      : ''
                  "
                  (valueChange)="onAutoSaveEmailChange($event)"
                  [tooltipTemplate]="autoSaveTooltipTemplate"
                  [showOn]="onboardingStore.inProgress() ? 'none' : 'hover'"
                  [tooltipWidth]="
                    autoSaveDisabled() ||
                    !emailSaveUserRights ||
                    autoSaveTooltipModeValue === 'syncEverything'
                      ? 260
                      : 115
                  "
                  [callout]="false"
                  [title]="
                    emailSaveUserRights
                      ? autoSaveDisabled()
                        ? null
                        : ('outlook.entryPage.autoSave.label' | translate)
                      : getSaveEmailPermissionMessage(type(), 'admin')
                  "
                ></kendo-switch>
              </span>
            </div>
            <p class="text-caption text-neutral-90 truncate">
              {{ 'outlook.entryPage.autoSave.label' | translate }}
            </p>
            <span
              class="maximizer-icons icon-12 icon-synced-email text-neutral-90 mt-0.5"
            ></span>
            <span
              kendoTooltip
              class="fa-icons icon-12 icon-solid icon-circle-question text-neutral-90"
              title="{{ 'outlook.entryPage.autoSave.help' | translate }}"
              [tooltipWidth]="250"
              [callout]="false"
              [tooltipTemplate]="autoSaveTooltip"
            ></span>
            @if (loading.sync) {
              <kendo-loader
                class="pl-4"
                type="pulsing"
                themeColor="primary"
                size="small"
              ></kendo-loader>
            }
          </div>

          <ng-template #autoSaveDisabledTooltip>
            <div class="ml-2 offset-left-tooltip">
              <p
                class="text-body"
                [innerHtml]="
                  'outlook.entryPage.autoSave.tooltip.emailSyncDisabled'
                    | translate
                "
              ></p>
            </div>
          </ng-template>

          <ng-template #autoSaveSyncEverythingEnabledTooltip>
            <div class="ml-2 offset-left-tooltip">
              <p
                class="text-body"
                [innerHtml]="
                  'outlook.entryPage.autoSave.tooltip.syncEverythingEnabled'
                    | translate
                      : {
                          individuals: 'individuals' | translateVertical,
                          entry: pageTitle,
                        }
                "
              ></p>
            </div>
          </ng-template>

          <ng-template #autoSaveSyncEverythingExcludedTooltip>
            <div class="ml-2 offset-left-tooltip">
              <p
                class="text-body"
                [innerHtml]="
                  'outlook.entryPage.autoSave.tooltip.syncEverythingExcluded'
                    | translate: { entry: pageTitle }
                "
              ></p>
            </div>
          </ng-template>

          <ng-template #installOutlookSyncTooltip>
            <maximizer-outlook-sync-install
              (closeTooltips)="closeTooltips()"
              [componentType]="'tooltip'"
            ></maximizer-outlook-sync-install>
          </ng-template>

          <ng-template #autoSaveTooltip>
            @if (!outlookSyncInstalled) {
              <p
                class="text-body"
                [innerHtml]="
                  'outlook.entryPage.autoSave.help.notInstalled' | translate
                "
              ></p>
            } @else if (
              outlookSyncInstalled &&
              emailSyncEnabled &&
              emailSyncSaveOptionEnabled
            ) {
              <p
                class="text-body"
                [innerHtml]="
                  'outlook.entryPage.autoSave.help.installed' | translate
                "
              ></p>
            } @else {
              <p
                class="text-body"
                [innerHtml]="
                  'outlook.entryPage.autoSave.help.disabled' | translate
                "
              ></p>
            }
          </ng-template>
        } @else if (outlookStore.isSharedMailbox()) {
          <maximizer-warning
            translationEntry="outlook.entryPage.autoSave.forbiddenSharedMailbox"
          />
        }
      }

      <!-- Actions row -->
      <div
        class="action-buttons flex transition-all border-2 py-1"
        [class]="
          (onboardingStore.inProgress() &&
          onboardingStore.currentGuide() === StepGuideId.EntryActions2
            ? 'border-pulse-enter rounded pointer-events-auto z-50 '
            : 'border-transparent ') +
          (lead ? 'justify-around' : 'justify-between')
        "
      >
        <!-- Save Email -->
        @if (showSaveEmail) {
          @if (isEmailSaved()) {
            <maximizer-circle-button
              kendoTooltip
              id="outlook-entry-ui-entryPage-emailSaved"
              [tooltipTemplate]="tooltipTemplate"
              [position]="getTooltipPosition()"
              [callout]="false"
              icon="saved-email"
              iconFamily="maximizer"
              size="small"
              [disabled]="true"
              [title]="'outlook.main.tooltip.email-saved' | translate"
            ></maximizer-circle-button>
          } @else {
            <maximizer-circle-button
              kendoTooltip
              id="outlook-entry-ui-entryPage-saveEmail"
              [tooltipTemplate]="tooltipTemplate"
              [position]="getTooltipPosition()"
              [callout]="false"
              icon="save-email"
              iconFamily="maximizer"
              iconSize="18"
              size="small"
              (click)="saveEmail()"
              (keydown.enter)="saveEmail()"
              [disabled]="disabled || !getSaveEmailPermission(type())"
              [title]="
                getSaveEmailPermission(type())
                  ? ('outlook.main.tooltip.save-email' | translate)
                  : getSaveEmailPermissionMessage(type(), 'default')
              "
            ></maximizer-circle-button>
          }
        }

        @if (isAbEntry() && aiIqBoostEnabled()) {
          <!-- Iq Boost -->
          <maximizer-circle-button
            kendoTooltip
            id="outlook-entry-ui-entryPage-iqBoost"
            [tooltipTemplate]="tooltipTemplate"
            position="bottom"
            [callout]="false"
            icon="iq-boost"
            iconFamily="maximizer"
            size="small"
            [routerLink]="disabled ? null : ['/iq-boost']"
            [disabled]="disabled"
            [title]="'outlook.iqBoost.title' | translate"
          ></maximizer-circle-button>
        }

        @if (isAbEntry()) {
          <!-- Add Opportunity -->
          <maximizer-circle-button
            kendoTooltip
            id="outlook-entry-ui-entryPage-addOpportunity"
            [tooltipTemplate]="tooltipTemplate"
            [position]="getTooltipPosition()"
            [callout]="false"
            icon="add-opportunity"
            iconFamily="maximizer"
            size="small"
            [routerLink]="
              disabled || !userRights()?.opportunities?.insert
                ? null
                : 'add-opportunity'
            "
            [disabled]="disabled || !userRights()?.opportunities?.insert"
            [title]="
              userRights()?.opportunities?.insert
                ? ('outlook.opportunity.addTitle'
                  | translate
                    : {
                        label: 'outlook.entries.opportunity' | translate,
                      })
                : ('outlook.rights.defaultMessage'
                  | translate
                    : {
                        right: 'outlook.rights.create' | translate,
                        entry: 'outlook.entries.opportunities' | translate,
                      })
            "
          ></maximizer-circle-button>
          <!-- Add Case -->
          @if (!aiIqBoostEnabled()) {
            <maximizer-circle-button
              kendoTooltip
              id="outlook-entry-ui-entryPage-addCase"
              [tooltipTemplate]="tooltipTemplate"
              [position]="getTooltipPosition()"
              [callout]="false"
              icon="add-case"
              iconFamily="maximizer"
              size="small"
              iconSize="18"
              [routerLink]="
                disabled || !userRights()?.customerService?.insert
                  ? null
                  : 'add-case'
              "
              [disabled]="disabled || !userRights()?.customerService?.insert"
              [title]="
                userRights()?.customerService?.insert
                  ? ('outlook.case.addTitle'
                    | translate
                      : {
                          label: 'case' | translateVertical,
                        })
                  : ('outlook.rights.defaultMessage'
                    | translate
                      : {
                          right: 'outlook.rights.create' | translate,
                          entry: 'cases' | translateVertical,
                        })
              "
            ></maximizer-circle-button>
          }
        }
        <!-- Add Note -->
        <maximizer-circle-button
          kendoTooltip
          id="outlook-entry-ui-entryPage-addNote"
          [tooltipTemplate]="tooltipTemplate"
          [position]="getTooltipPosition()"
          [callout]="false"
          icon="add-note"
          iconFamily="maximizer"
          size="small"
          [routerLink]="
            disabled || !userRights()?.notes?.insert ? null : 'add-note'
          "
          [disabled]="disabled || !userRights()?.notes?.insert"
          [ngClass]="{
            'pointer-events-auto z-50': onboardingStore.inProgress(),
          }"
          [title]="
            userRights()?.notes?.insert
              ? ('outlook.note.addTitle' | translate)
              : ('outlook.rights.defaultMessage'
                | translate
                  : {
                      right: 'outlook.rights.create' | translate,
                      entry: 'outlook.entries.notes' | translate,
                    })
          "
        >
        </maximizer-circle-button>
        <!-- Add Task -->
        <maximizer-circle-button
          kendoTooltip
          id="outlook-entry-ui-entryPage-addTask"
          [tooltipTemplate]="tooltipTemplate"
          [position]="getTooltipPosition()"
          [callout]="false"
          icon="add-task"
          iconFamily="maximizer"
          size="small"
          [routerLink]="disabled ? null : 'add-task'"
          [disabled]="disabled"
          [title]="'outlook.task.addTitle' | translate"
        ></maximizer-circle-button>
        @if (!isAbEntry()) {
          <!-- Add Call -->
          <maximizer-circle-button
            kendoTooltip
            id="outlook-entry-ui-entryPage-addCall"
            [tooltipTemplate]="tooltipTemplate"
            [position]="getTooltipPosition()"
            [callout]="false"
            icon="add-call"
            iconFamily="maximizer"
            size="small"
            [routerLink]="
              disabled || !userRights()?.interactionLog?.insert
                ? null
                : 'add-call-log'
            "
            [disabled]="disabled || !userRights()?.interactionLog?.insert"
            [title]="
              userRights()?.interactionLog?.insert
                ? ('outlook.callLog.addTitle' | translate)
                : ('outlook.rights.defaultMessage'
                  | translate
                    : {
                        right: 'outlook.rights.create' | translate,
                        entry:
                          'outlook.callLog.callLogs' | translate | lowercase,
                      })
            "
          ></maximizer-circle-button>
          <!-- Add Appointment -->
          <maximizer-circle-button
            kendoTooltip
            id="outlook-entry-ui-entryPage-addAppointment"
            [tooltipTemplate]="tooltipTemplate"
            [position]="getTooltipPosition()"
            [callout]="false"
            icon="add-appointment"
            iconFamily="maximizer"
            size="small"
            [routerLink]="disabled ? null : 'add-appointment'"
            [disabled]="disabled"
            [title]="'outlook.appointment.addTitle' | translate"
          ></maximizer-circle-button>
        } @else {
          <kendo-dropdownbutton
            #moreActionsDropdown
            kendoTooltip
            [tooltipTemplate]="tooltipTemplate"
            [position]="getTooltipPosition()"
            [callout]="false"
            [tooltipWidth]="110"
            [title]="'outlook.entryPage.moreActions' | translate"
            class="w-8 h-8 rounded-full bg-primary-100 text-bush-green-140 hover:shadow-circle flex justify-center items-center"
            [data]="moreActions()"
            fillMode="flat"
            iconClass="action-dropdown fa-icons icon-16 icon-ellipsis text-bush-green-140"
            (open)="onMoreActionsOpen()"
            (close)="onMoreActionsClose()"
            [popupSettings]="{
              appendTo: viewContainerRef,
              popupClass: 'z-[50]',
            }"
          >
            <ng-template kendoDropDownButtonItemTemplate let-dataItem>
              <div
                kendoTooltip
                [tooltipTemplate]="tooltipTemplate"
                position="top"
                [callout]="false"
                [showOn]="dataItem.disabled ? 'hover' : 'none'"
                [title]="
                  'outlook.rights.defaultMessage'
                    | translate
                      : {
                          right: 'outlook.rights.create' | translate,
                          entry: dataItem.title | translate,
                        }
                "
                id="outlook-entry-ui-entryPage-moreOptions{{ dataItem.tag }}"
                class="action-dropdown flex items-center gap-1 w-full"
                (click)="dataItem.click()"
                (keydown.enter)="dataItem.click()"
              >
                <span [class]="dataItem.icon" class="w-4"></span>
                <span>{{ dataItem.text }}</span>
              </div>
            </ng-template>
          </kendo-dropdownbutton>
        }
      </div>
      <div
        kendoPopoverContainer
        [popover]="entryActions2"
        #entryActions2Anchor
        #entryActions2Container="kendoPopoverContainer"
        showOn="none"
        class="flex justify-center"
      ></div>
    </div>
  }

  <!-- Entry Tabs -->
  <kendo-tabstrip
    class="w-full flex-1 flex sticky top-0 bg-white entry-page"
    tabAlignment="justify"
    (tabSelect)="onTabSelect($event)"
  >
    <!-- Summary -->
    <kendo-tabstrip-tab
      [selected]="true"
      title="summary"
      cssClass="flex-1 gap-0"
    >
      <ng-template kendoTabTitle>
        <div id="outlook-entry-ui-entryPage-summary" class="w-full text-center">
          <p class="text-body">
            {{ 'outlook.entryPage.summary' | translate }}
          </p>
        </div>
      </ng-template>
      <ng-template kendoTabContent>
        <maximizer-entry-summary
          [id]="currentId()"
          [type]="type()"
          [lead]="lead"
          [abentry]="abentry"
          [deepLink]="deepLink"
          [oppLink]="oppLink"
          [csLink]="csLink"
        ></maximizer-entry-summary>
      </ng-template>
    </kendo-tabstrip-tab>
    <!-- Timeline -->
    <kendo-tabstrip-tab title="timeline" cssClass="flex-1 gap-0">
      <ng-template kendoTabTitle>
        <div
          id="outlook-entry-ui-entryPage-timeline"
          class="w-full text-center"
        >
          <p class="text-body">
            {{ 'outlook.entryPage.timeline' | translate }}
          </p>
        </div>
      </ng-template>
      <ng-template kendoTabContent>
        <maximizer-outlook-timeline
          #timelineComponent
          class="h-full"
          [id]="currentId()"
          [type]="type()"
        ></maximizer-outlook-timeline>
      </ng-template>
    </kendo-tabstrip-tab>
    <!-- Details -->
    <kendo-tabstrip-tab title="details" cssClass="flex-1 gap-0">
      <ng-template kendoTabTitle>
        <div id="outlook-entry-ui-entryPage-details" class="w-full text-center">
          <p class="text-body">
            {{ 'outlook.entryPage.details' | translate }}
          </p>
        </div>
      </ng-template>
      <ng-template kendoTabContent>
        <maximizer-entry-details
          [id]="currentId()"
          [type]="type()"
        ></maximizer-entry-details>
      </ng-template>
    </kendo-tabstrip-tab>
  </kendo-tabstrip>

  @if (scrollToTopVisible) {
    <div class="fixed bottom-10 right-10">
      <maximizer-circle-button
        themeColor="tertiary"
        icon="arrow-up"
        size="small"
        [title]="'outlook.buttons.scrollToTop' | translate"
        (click)="scrollToTop()"
        (keydown.enter)="scrollToTop()"
      ></maximizer-circle-button>
    </div>
  }
</div>

@if (showAutoSaveLimitDialog) {
  <kendo-dialog
    id="email-sync-limit-dialog"
    [minWidth]="270"
    [minHeight]="180"
    maxWidth="95%"
    [width]="270"
    (close)="closeEmailSyncLimitDialog()"
    [themeColor]="'light'"
  >
    <kendo-dialog-titlebar>
      <span class="subtitle-2 px-2">{{
        'outlook.entryPage.autoSave.limitDialogTitle' | translate
      }}</span>
    </kendo-dialog-titlebar>
    <kendo-dialog-messages>
      <span class="text-body whitespace-break-spaces">{{
        'outlook.entryPage.autoSave.limitDialogDescription' | translate
      }}</span>
    </kendo-dialog-messages>

    <div class="flex mt-4">
      <button
        kendoButton
        themeColor="secondary"
        [title]="'outlook.buttons.close' | translate"
        (click)="closeEmailSyncLimitDialog()"
        class="w-20 mr-auto"
      >
        {{ 'outlook.buttons.close' | translate }}
      </button>

      <button
        kendoButton
        (click)="reviewListEmailSyncDialog()"
        themeColor="primary"
        [title]="'outlook.buttons.reviewList' | translate"
        class="mr-2 w-28"
      >
        {{ 'outlook.buttons.reviewList' | translate }}
      </button>
    </div>
  </kendo-dialog>
}

<maximizer-outlook-notification
  #notification
  [closable]="true"
  [entryId]="entryType"
>
</maximizer-outlook-notification>

<ng-template #tooltipTemplate let-anchor>
  <p class="caption text-neutral-90 text-center">
    {{ anchor.nativeElement.getAttribute('data-title') }}
  </p>
</ng-template>

@if (saveEmailDetail) {
  <maximizer-email-detail
    [emailProperties]="saveEmailDetailProperties"
    (saveEmailKey)="onSaveEmailDetail()"
    (closed)="this.saveEmailDetail = false"
  ></maximizer-email-detail>
}

<!-- Onboarding | Step 3: Guide 2 -->
<kendo-popover
  #entryActions2
  [position]="popoverPosition"
  [width]="stepPopoverWidth"
  [offset]="OnboardingGuideOffsets.ENTRY_ACTIONS_2"
>
  <ng-template kendoPopoverBodyTemplate>
    <maximizer-outlook-onboarding-step-guide
      [guide]="onboardingStore.getGuideForStep(StepGuideId.EntryActions2)"
      (exit)="onboardingService.showExitDialog.set(true)"
      (next)="nextOnboardingStep()"
    ></maximizer-outlook-onboarding-step-guide>
  </ng-template>
</kendo-popover>

<!-- Onboarding | Step 3: Guide 3 -->
<kendo-popover
  #entryActions3
  [position]="popoverPosition"
  [width]="stepPopoverWidth"
  [offset]="OnboardingGuideOffsets.ENTRY_ACTIONS_3"
>
  <ng-template kendoPopoverBodyTemplate>
    <maximizer-outlook-onboarding-step-guide
      class="popover-left-margin"
      [guide]="
        onboardingStore.getGuideForStep(
          onboardingStore.currentGuide() === StepGuideId.EntryActions3Alt
            ? StepGuideId.EntryActions3Alt
            : StepGuideId.EntryActions3
        )
      "
      (exit)="onboardingService.showExitDialog.set(true)"
      (next)="nextOnboardingStep()"
    ></maximizer-outlook-onboarding-step-guide>
  </ng-template>
</kendo-popover>
